'use client';

import type { ReactNode } from 'react';

import { Label } from '@eliadmin/core/src/components/ui/label';
import { FormControl, FormItem, useFormField } from '@eliadmin/core/src/components/ui/form';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

type FormRowProps = {
  label: ReactNode;
  required?: boolean;
  /** Helper text under the field (Figma „Helper text“), replaced by the error while there is one. */
  hint?: ReactNode;
  className?: string;
  /** Exactly one element that takes `id` and `aria-*` props – the DLZ `Input`, `PasswordInput`, … */
  children: ReactNode;
};

function FormRowBody({ label, required, hint, children }: Omit<FormRowProps, 'className'>) {
  const { t } = useTranslations();
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();
  const message = error?.message ? t(String(error.message)) : null;

  return (
    <>
      <Label htmlFor={formItemId} required={required}>
        {label}
      </Label>
      <FormControl aria-required={required || undefined}>{children}</FormControl>
      {message ? (
        <p
          id={formMessageId}
          role="alert"
          className="dlz-caption whitespace-pre-line text-[#D7191F]"
        >
          {message}
        </p>
      ) : hint ? (
        <p id={formDescriptionId} className="dlz-caption text-[#6B7079]">
          {hint}
        </p>
      ) : null}
    </>
  );
}

/**
 * Figma „Input“ anatomy around any control: label row (DLZ/Body/S Medium, red „*“), 4 px, the field,
 * 4 px, helper text 12 px text/tertiary – or the error in text/brand. Must sit inside a react-hook-form
 * `FormField` render, which supplies the ids and the error.
 */
export function FormRow({ className, ...props }: FormRowProps) {
  return (
    <FormItem className={cn('flex flex-col gap-1', className)}>
      <FormRowBody {...props} />
    </FormItem>
  );
}
